/**
 * depotPhases — progression unifiée des dépôts (13/07/2026).
 *
 * TOUS les dépôts (QG, créés, rachetés) évoluent désormais via le MÊME
 * arbre d'investissements (data/investments.js, catégories Dépôt/Énergie).
 *   - Le QG affiche les 14 scènes pré-rendues /depot-scene-v3/phase-XX.webp.
 *   - Les dépôts secondaires (marque concurrente ou 'independant') affichent
 *     7 scènes jalons brandées /depot-scene-v3/brands/{brand}-p{1..7}.webp,
 *     sur lesquelles les 14 phases sont mappées (brandMilestoneFromPhase).
 *   - Un dépôt racheté démarre avec des investissements pré-acquis selon la
 *     taille de sa flotte au closing (seedDepotInvestmentsForFleet) : il
 *     reprend son évolution là où le concurrent en était.
 *   - L'ancien système DEPOT_UPGRADES (6 évolutions satellites) est déprécié;
 *     les sauvegardes existantes sont mappées via LEGACY_UPGRADE_TO_INVESTMENT.
 */

/**
 * Canonical build order — 14 phases following the depot upgrade tree.
 * The displayed image is the MAX phase whose driving investment is active
 * (out-of-order purchases always reveal the newest building).
 */
export const DEPOT_PHASE_INVESTMENTS = [
  ['inv-depot-01', 2],    // Garage agrandi ×2
  ['inv-depot-02', 3],    // Entrepôt
  ['inv-depot-04', 4],    // Quai de chargement (accolé à l'entrepôt)
  ['inv-depot-03', 5],    // Entrepôt agrandi (terrain)
  ['inv-depot-05', 6],    // Zone frigorifique
  ['inv-depot-07', 7],    // Quai renforcé
  ['inv-depot-08', 8],    // Entrepôt grand gabarit
  ['inv-depot-11', 9],    // Borne de recharge
  ['inv-energy-02', 10],  // Pompe à carburant
  ['inv-depot-12', 11],   // Atelier mécanique
  ['inv-depot-10', 12],   // Zone ADR
  ['inv-depot-solar', 13],// Panneaux solaires atelier
  ['inv-energy-01', 14],  // Station carburant agrandie
];

export const DEPOT_PHASE_MAX = 14;

export function depotPhaseFromInvestments(activeInvestments) {
  let maxPhase = 1;
  for (const [inv, idx] of DEPOT_PHASE_INVESTMENTS) {
    if (activeInvestments?.[inv] && idx > maxPhase) maxPhase = idx;
  }
  return maxPhase;
}

export function depotPhaseImage(phase) {
  return `/depot-scene-v3/phase-${String(phase).padStart(2, '0')}.webp`;
}

/* ── Jalons visuels brandés (dépôts secondaires) ─────────────────────── */

// Phase seuil de chaque jalon m2..m7 (m1 = base). m1: 0-2, m2: 3-4,
// m3: 5-6, m4: 7-8, m5: 9-10, m6: 11-13, m7: 14.
export const BRAND_MILESTONE_THRESHOLDS = [3, 5, 7, 9, 11, 14];
export const BRAND_MILESTONE_MAX = 7;

export function brandMilestoneFromPhase(phase) {
  let m = 1;
  BRAND_MILESTONE_THRESHOLDS.forEach(t => { if ((phase || 0) >= t) m += 1; });
  return Math.min(m, BRAND_MILESTONE_MAX);
}

export function depotBrandPhaseImage(brand, phase) {
  return `/depot-scene-v3/brands/${brand}-p${brandMilestoneFromPhase(phase)}.webp`;
}

/* ── Compat sauvegardes : anciennes évolutions satellites ────────────── */

export const LEGACY_UPGRADE_TO_INVESTMENT = {
  radiusExtension: 'inv-depot-06',
  quai: 'inv-depot-04',
  frigoLocal: 'inv-depot-05',
  atelier: 'inv-depot-12',
  solar: 'inv-depot-solar',
  borne: 'inv-energy-02',
};

/**
 * Investissements effectifs d'un dépôt secondaire : ceux achetés via le
 * système unifié (`depot.investments`) + les anciennes évolutions legacy
 * (`depot.upgrades`) mappées sur leurs équivalents investissements.
 */
export function getDepotEffectiveInvestments(depot) {
  const map = {};
  Object.entries(depot?.investments || {}).forEach(([k, v]) => { if (v) map[k] = true; });
  Object.entries(depot?.upgrades || {}).forEach(([k, v]) => {
    if (v && LEGACY_UPGRADE_TO_INVESTMENT[k]) map[LEGACY_UPGRADE_TO_INVESTMENT[k]] = true;
  });
  return map;
}

/**
 * État de départ d'un dépôt RACHETÉ : le concurrent a déjà développé son
 * site proportionnellement à sa flotte. Petit concurrent (~2 véh.) ≈ phase 3,
 * gros (~8 véh.) ≈ phase 9. Plafonné à la phase 11 : ADR, solaire et station
 * agrandie restent à financer par le joueur.
 */
export function seedDepotInvestmentsForFleet(vehiclesCount) {
  const target = Math.max(2, Math.min(11, 1 + (vehiclesCount || 0)));
  const map = {};
  DEPOT_PHASE_INVESTMENTS.forEach(([inv, idx]) => { if (idx <= target) map[inv] = true; });
  return map;
}
